import { useCallback } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useWithdrawBalance } from '@package/smart-wallet/hooks';
import { isEvmAddress } from '@package/smart-wallet/utils';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { LoaderSkeleton } from './LoaderSkeleton';

/* Types & Schemas */
export const WithdrawBalanceFormSchema = z.object({
  amount: z
    .string({
      message: 'Amount is required',
    })
    .min(1, { message: 'Amount cannot be empty' })
    .refine(
      (amount) => {
        return !isNaN(Number(amount));
      },
      { message: 'Amount must be a number' },
    ),
  address: z
    .string({
      message: 'Address is required',
    })
    .min(1, { message: 'Address cannot be empty' })
    .refine(
      (address) => {
        return isEvmAddress(address);
      },
      {
        message: 'Invalid wallet address format',
      },
    ),
});

export type WithdrawBalanceFormType = z.infer<typeof WithdrawBalanceFormSchema>;

/* Internal components */
type WithdrawFormProps = {
  handleWithdrawBalance: (
    tokenSymbol: string,
    amount: string,
    address: string,
  ) => Promise<`0x${string}` | undefined>;
};

const WithdrawForm = ({ handleWithdrawBalance }: WithdrawFormProps) => {
  const form = useForm<WithdrawBalanceFormType>({
    resolver: zodResolver(WithdrawBalanceFormSchema),
  });

  /* Callbacks */
  const onSubmit = useCallback(
    async (data: WithdrawBalanceFormType) => {
      try {
        const { amount, address } = data;
        // Default to USDT for sandbox component
        const txHash = await handleWithdrawBalance('USDT', amount, address);

        console.log('=== txHash ===', txHash);
        form.reset();
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [form, handleWithdrawBalance],
  );

  /* Render */
  return (
    <Form {...form}>
      <form
        className='w-full flex flex-col gap-2'
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name='amount'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{'Amount'}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder='0.00'
                  type='text'
                  pattern='^\d*\.?\d*$'
                  inputMode='decimal'
                />
              </FormControl>
              <FormMessage className='text-start' />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='address'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{'Recipient address'}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder='0x0000000000000000000000000000000000000000'
                />
              </FormControl>
              <FormMessage className='text-start' />
            </FormItem>
          )}
        />
        <Button
          className='w-full cursor-pointer uppercase'
          type='submit'
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? 'withdrawing...' : 'withdraw'}
        </Button>
      </form>
    </Form>
  );
};

/* Main component */
export const WithdrawBalance = () => {
  const { isLoading, handleWithdrawBalance } = useWithdrawBalance();

  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      {isLoading && <LoaderSkeleton />}
      {!isLoading && (
        <WithdrawForm handleWithdrawBalance={handleWithdrawBalance} />
      )}
    </section>
  );
};
